import { useTranslation } from 'react-i18next'
import { InfoIcon } from './icons/InfoIcon'
import { InfoTooltip } from './InfoTooltip'

/* Shared "Agent Summary" textarea field — used by SkillForm (MS-734) and
 * KnowledgeSourceForm (MS-866). Same markup/styles for both, only the i18n
 * keys and error differ, so this is the single source rather than each form
 * hand-rolling an identical block. */

type LabelKey = 'skillsTab.editor.agentSummaryLabel' | 'knowledgeTab.editor.agentSummaryLabel'
type PlaceholderKey = 'skillsTab.editor.agentSummaryPlaceholder' | 'knowledgeTab.editor.agentSummaryPlaceholder'
type TooltipKey = 'skillsTab.editor.agentSummaryTooltip' | 'knowledgeTab.editor.agentSummaryTooltip'

export interface AgentSummaryFieldProps {
  idPrefix: string
  value: string
  onChange: (value: string) => void
  labelKey: LabelKey
  placeholderKey: PlaceholderKey
  tooltipKey: TooltipKey
  /** Already-translated error message, or undefined/falsy when valid. */
  error?: string
}

export function AgentSummaryField({
  idPrefix,
  value,
  onChange,
  labelKey,
  placeholderKey,
  tooltipKey,
  error,
}: Readonly<AgentSummaryFieldProps>) {
  const { t } = useTranslation()
  return (
    <div className={`form-group${error ? ' form-group-invalid' : ''}`}>
      <label htmlFor={`${idPrefix}-agentsummary`} className="form-label">
        {t(labelKey)}
        <span className="required-mark" aria-hidden="true">*</span>{' '}
        <InfoTooltip width={300}>{t(tooltipKey)}</InfoTooltip>
      </label>
      <textarea
        id={`${idPrefix}-agentsummary`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t(placeholderKey)}
        className="form-input"
        style={{ maxWidth: '100%', resize: 'vertical', boxSizing: 'border-box', minHeight: 36, height: 56 }}
        aria-invalid={!!error}
        aria-describedby={error ? `${idPrefix}-agentsummary-error` : undefined}
      />
      {error && (
        <div className="field-error" role="alert">
          <InfoIcon size={12} />
          {error}
        </div>
      )}
    </div>
  )
}
